import { useEffect, useState } from 'react';
import { Icon } from '@mui/material';
import Modal from 'react-modal';
import { Button, InputField, Typography } from 'components/common';
import { useFormik } from 'formik';
import { IReservation } from 'types';
import { toast } from 'react-toastify';
import * as api from 'api';
import Dropzone from 'components/common/Dropzone';
import variables from 'assets/scss/variables.scss';

interface IProps {
  visible?: boolean;
  reservation: IReservation;
  onClose?: () => void;
  onSuccess?: (reservation: IReservation) => void;
}

const AddTolls = ({
  visible = false,
  reservation,
  onClose,
  onSuccess,
}: IProps) => {
  const [showModal, setShowModal] = useState(visible);

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  useEffect(() => {
    if (!showModal && onClose) {
      onClose();
    }
  }, [showModal]);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      tolls: [
        {
          start_name: '',
          end_name: '',
          start_timestamp: '',
          end_timestamp: '',
          amount: 0,
        },
      ],
      toll_images: [],
    },
    validate: values => {
      const error = {} as any;

      if (values.tolls.length === 0) {
        error.tolls = 'At least one toll is required';
      }

      values.tolls.forEach((toll: any, index: number) => {
        if (!toll.start_name) {
          error.tolls = error.tolls || [];
          error.tolls[index] = {
            ...error.tolls[index],
            start_name: true,
          };
        }

        if (!toll.end_name) {
          error.tolls = error.tolls || [];
          error.tolls[index] = {
            ...error.tolls[index],
            end_name: true,
          };
        }

        if (!toll.start_timestamp) {
          error.tolls = error.tolls || [];
          error.tolls[index] = {
            ...error.tolls[index],
            start_timestamp: true,
          };
        }

        if (!toll.end_timestamp) {
          error.tolls = error.tolls || [];
          error.tolls[index] = {
            ...error.tolls[index],
            end_timestamp: true,
          };
        }

        if (
          toll.start_timestamp
          && toll.end_timestamp
          && new Date(toll.start_timestamp) > new Date(toll.end_timestamp)
        ) {
          error.tolls = error.tolls || [];
          error.tolls[index] = {
            ...error.tolls[index],
            start_timestamp: true,
            end_timestamp: true,
          };
        }

        if (!toll.amount || toll.amount <= 0) {
          error.tolls = error.tolls || [];
          error.tolls[index] = {
            ...error.tolls[index],
            amount: true,
          };
        }
      });

      if (values.toll_images.length === 0) {
        error.toll_images = 'At least one image is required';
      }

      return error;
    },
    onSubmit: async (values) => {
      const payload = {
        tolls: values.tolls,
        toll_images: values.toll_images,
      };

      try {
        const data = await api.updateOwnerReservation({ payload, reservationId: reservation!.id });
        // eslint-disable-next-line no-unused-expressions
        onSuccess && onSuccess(data);
        toast.success('Reservation updated successfully');
        resetForm();
        setShowModal(false);
      } catch (error: any) {
        toast.error(error.response.data.error);
      }
    },
  });

  return (
    <Modal
      ariaHideApp={false}
      isOpen={showModal}
      style={{
        overlay: {
          zIndex: 1000,
        },
        content: {
          zIndex: 1000,
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          padding: '29px',
          maxWidth: '90%',
          minWidth: '300px',
          minHeight: '500px',
          maxHeight: '90%',
        },
      }}
      onRequestClose={() => {
        resetForm();
        setShowModal(false);
        // eslint-disable-next-line no-unused-expressions
        onClose && onClose();
      }}
    >
      <div className="d-flex justify-content-end">
        <Button
          onClick={() => {
            resetForm();
            setShowModal(false);
            // eslint-disable-next-line no-unused-expressions
            onClose && onClose();
          }}
          style={{ width: '25px', height: '25px', padding: 0 }}
        >
          X
        </Button>
      </div>

      <div className="d-flex flex-column w-100 align-items-center">
        <Typography variant="h1">
          Add Tolls
        </Typography>
      </div>

      <div style={{ height: '100%', overflowY: 'scroll' }}>
        <div style={{ height: '80%' }}>
          <div>
            {values.tolls.length > 0 && values.tolls.map((toll: any, index: number) => (
              <div
                className="d-flex flex-row justify-content-space-between align-items-center gap-2"
                style={{ overflowX: 'scroll' }}
                key={index}
              >
                <div>
                  <InputField
                    className="mb-2"
                    label="Entry Plaza Name"
                    type="text"
                    name={`tolls.${index}.start_name`}
                    value={toll.start_name}
                    error={
                      touched.tolls
                      && touched.tolls[index]?.start_name
                      && errors.tolls
                      && (errors.tolls[index] as any)?.start_name
                    }
                    onChange={handleChange}
                    required
                    style={{ width: '200px' }}
                  />
                </div>

                <div>
                  <InputField
                    className="mb-2"
                    label="Exit Plaza Name"
                    type="text"
                    name={`tolls.${index}.end_name`}
                    value={toll.end_name}
                    error={
                      touched.tolls
                      && touched.tolls[index]?.end_name
                      && errors.tolls
                      && (errors.tolls[index] as any)?.end_name
                    }
                    onChange={handleChange}
                    required
                    style={{ width: '200px' }}
                  />
                </div>

                <div>
                  <InputField
                    className="mb-2"
                    label="Entry Date"
                    type="datetime-local"
                    name={`tolls.${index}.start_timestamp`}
                    value={toll.start_timestamp}
                    error={
                      touched.tolls
                      && touched.tolls[index]?.start_timestamp
                      && errors.tolls
                      && (errors.tolls[index] as any)?.start_timestamp
                    }
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <InputField
                    className="mb-2"
                    label="Exit Date"
                    type="datetime-local"
                    name={`tolls.${index}.end_timestamp`}
                    value={toll.end_timestamp}
                    error={
                      touched.tolls
                      && touched.tolls[index]?.end_timestamp
                      && errors.tolls
                      && (errors.tolls[index] as any)?.end_timestamp
                    }
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <InputField
                    className="mb-2"
                    label="Amount"
                    type="number"
                    name={`tolls.${index}.amount`}
                    value={toll.amount}
                    error={
                      touched.tolls
                      && touched.tolls[index]?.amount
                      && errors.tolls
                      && (errors.tolls[index] as any)?.amount
                    }
                    onChange={handleChange}
                    required
                    style={{ width: '125px' }}
                  />
                </div>

                <div>
                  <Icon
                    onClick={() => {
                      const newToll = {
                        start_name: '',
                        end_name: '',
                        start_timestamp: '',
                        end_timestamp: '',
                        amount: 0,
                      };
                      setFieldValue(
                        'tolls',
                        [...values.tolls.slice(0, index + 1), newToll, ...values.tolls.slice(index + 1)],
                      );
                    }}
                  >
                    add
                  </Icon>
                </div>
                {
                  values.tolls.length > 1 && (
                    <div>
                      <Icon
                        onClick={() => {
                          if (values.tolls.length > 1) {
                            setFieldValue(
                              'tolls',
                              [...values.tolls.slice(0, index), ...values.tolls.slice(index + 1)],
                            );
                          }
                        }}
                      >
                        remove
                      </Icon>
                    </div>
                  )
                }
              </div>
            ))}
          </div>
        </div>

        <div>
          <Dropzone
            accept={{
              'image/png': ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
              'application/pdf': ['.pdf'],
            }}
            onImagesChanged={(files: any[]) => {
              setFieldValue('toll_images', files);
            }}
            defaultFiles={values.toll_images || []}
            convertImgToJpeg
          />

          {
            errors.toll_images && (
              <div
                className="pt-2"
                style={{
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  color: variables.red1,
                }}
              >
                {errors.toll_images}
              </div>
            )
          }
        </div>

        <div className="d-flex w-100 justify-content-space-around mt-2">
          <Button
            onClick={handleSubmit}
          >
            Set New Tolls
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddTolls;
